export const CPF_VALIDATION_REGEX = /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2})$/
export const CNPJ_VALIDATION_REGEX =
  /^([A-Z0-9]{2}\.?[A-Z0-9]{3}\.?[A-Z0-9]{3}\/?[A-Z0-9]{4}-?\d{2})$/i
export const CPF_CNPJ_VALIDATION_REGEX =
  /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2})$|^([A-Z0-9]{2}\.?[A-Z0-9]{3}\.?[A-Z0-9]{3}\/?[A-Z0-9]{4}-?\d{2})$/i
export const PHONE_VALIDATION_REGEX = /^(?:55)?\d{2}(?:9\d{8}|[2-5]\d{7})$/
export const PHONE_ONLY_NUMBER_VALIDATION_REGEX = /^(?:9\d{8}|[2-5]\d{7})$/

export const stripNonDigits = (value: string): string => (value ?? '').replace(/\D/g, '')

export const stripNonAlphanumeric = (value: string): string =>
  (value ?? '').replace(/[^A-Z0-9]/gi, '').toUpperCase()

const hasRepeatedChars = (chars: string, length: number) =>
  chars.length === length && new Set(chars).size === 1

const calcCPFDigit = (digits: string, length: number) =>
  ((digits
    .slice(0, length)
    .split('')
    .reduce((sum, d, i) => sum + Number(d) * (length + 1 - i), 0) *
    10) %
    11) %
  10

const isValidCPFDigits = (digits: string): boolean => {
  if (digits.length !== 11 || hasRepeatedChars(digits, 11)) return false
  return (
    calcCPFDigit(digits, 9) === Number(digits[9]) && calcCPFDigit(digits, 10) === Number(digits[10])
  )
}

const charToValue = (char: string): number => char.charCodeAt(0) - 48

const CNPJ_WEIGHTS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const
const CNPJ_WEIGHTS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const

const calcCNPJDigit = (chars: string, weights: readonly number[]) => {
  const sum = chars
    .slice(0, weights.length)
    .split('')
    .reduce((acc, c, i) => acc + charToValue(c) * weights[i], 0)
  const remainder = sum % 11
  return remainder < 2 ? 0 : 11 - remainder
}

const isValidCNPJChars = (chars: string): boolean => {
  if (chars.length !== 14) return false
  // Last 2 characters must be numeric (check digits)
  if (!/^\d{2}$/.test(chars.slice(12))) return false
  // Reject all-identical sequences
  if (hasRepeatedChars(chars, 14)) return false
  return (
    calcCNPJDigit(chars, CNPJ_WEIGHTS_1) === Number(chars[12]) &&
    calcCNPJDigit(chars, CNPJ_WEIGHTS_2) === Number(chars[13])
  )
}

export function isValidCPF(value: string): boolean {
  return isValidCPFDigits(stripNonDigits(value))
}

export function isValidCNPJ(value: string): boolean {
  return isValidCNPJChars(stripNonAlphanumeric(value))
}

export const isValidPhoneNumber = (value: string, onlyNumber = false): boolean => {
  if (!value) return true
  return onlyNumber
    ? PHONE_ONLY_NUMBER_VALIDATION_REGEX.test(stripNonDigits(value))
    : PHONE_VALIDATION_REGEX.test(stripNonDigits(value))
}

export function isValidTaxDocument(value: string): boolean {
  const alphanumeric = stripNonAlphanumeric(value)
  if (alphanumeric.length === 14) return isValidCNPJChars(alphanumeric)
  const digits = stripNonDigits(value)
  return digits.length === 11 && isValidCPFDigits(digits)
}
